import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { CalendarModule } from 'primeng/calendar';
import { PrimeNGConfig } from 'primeng/api';
import { GenericCrudService } from '../../../../services/generic-crud.service';
import { CustomDropdownComponent } from '../../../../shared/components/form-controls/custom-dropdown/custom-dropdown.component';
import { environment } from '../../../../../environments/environment';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-gestion-masiva',
  standalone: true,
  imports: [CommonModule, FormsModule, CalendarModule, CustomDropdownComponent],
  templateUrl: './gestion-masiva.component.html',
  styleUrls: ['./gestion-masiva.component.css']
})
export class GestionMasivaComponent implements OnInit {

  // Opciones de dropdowns
  teachers: any[] = [];
  vehicles: any[] = [];
  statuses = [
    { label: 'Disponible', value: 'Disponible' },
    { label: 'Confirmada', value: 'Confirmada' },
    { label: 'Cancelado', value: 'Cancelado' }
  ];

  // Filtros
  selectedTeacherId: number | null = null;
  dateRange: Date[] | null = null;
  filterStatus: string | null = null;

  // Resultados y seleccion
  schedules: any[] = [];
  selectedIds: Set<number> = new Set();
  allSelected: boolean = false;

  // Estados de carga
  loading: boolean = false;
  loadingAction: boolean = false;

  // Acciones masivas
  bulkStatus: string | null = null;
  bulkVehicleId: number | null = null;
  bulkStartTime: string = '';
  bulkEndTime: string = '';
  bulkMaxStudents: number | null = null;

  // Modal de confirmacion
  showDeleteConfirm: boolean = false;

  // Filtros de selección por día
  dayFilterButtons = [
    { id: 1, label: 'Lunes', shortLabel: 'Lun', active: false },
    { id: 2, label: 'Martes', shortLabel: 'Mar', active: false },
    { id: 3, label: 'Miercoles', shortLabel: 'Mie', active: false },
    { id: 4, label: 'Jueves', shortLabel: 'Jue', active: false },
    { id: 5, label: 'Viernes', shortLabel: 'Vie', active: false },
    { id: 6, label: 'Sabado', shortLabel: 'Sab', active: false },
    { id: 0, label: 'Domingo', shortLabel: 'Dom', active: false },
  ];

  constructor(
    private http: HttpClient,
    private genericCrudService: GenericCrudService,
    private toastr: ToastrService,
    private primengConfig: PrimeNGConfig
  ) {}

  ngOnInit(): void {
    this.primengConfig.setTranslation({
      firstDayOfWeek: 1,
      dayNames: ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'],
      dayNamesShort: ['dom', 'lun', 'mar', 'mie', 'jue', 'vie', 'sab'],
      dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
      monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
      monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
      today: 'Hoy',
      clear: 'Limpiar',
      dateFormat: 'dd/mm/yy',
      weekHeader: 'Sem'
    });

    this.loadOptions();
  }

  loadOptions(): void {
    forkJoin({
      teachers: this.genericCrudService.list('/user/teachers'),
      vehicles: this.genericCrudService.list('/config/vehicles/listAll')
    }).subscribe({
      next: (res) => {
        this.teachers = res.teachers.data || [];
        this.vehicles = res.vehicles.data || [];
      },
      error: () => {
        this.toastr.error('Error al cargar las opciones');
      }
    });
  }

  search(): void {
    if (!this.selectedTeacherId) {
      this.toastr.warning('Selecciona un profesor');
      return;
    }

    if (!this.dateRange || this.dateRange.length < 2 || !this.dateRange[0] || !this.dateRange[1]) {
      this.toastr.warning('Selecciona un rango de fechas');
      return;
    }

    this.loading = true;
    this.clearSelection();
    this.clearDayFilters();

    let url = `${environment.API_URL}/practice-schedules?teacher_id=${this.selectedTeacherId}&start_date=${this.formatDate(this.dateRange[0])}&end_date=${this.formatDate(this.dateRange[1])}`;

    if (this.filterStatus) {
      url += `&status=${this.filterStatus}`;
    }

    this.http.get<any>(url).subscribe({
      next: (res) => {
        this.loading = false;
        this.schedules = res.data || [];
        if (this.schedules.length === 0) {
          this.toastr.info('No se encontraron clases para los filtros seleccionados');
        }
      },
      error: (err) => {
        this.loading = false;
        this.toastr.error(err.error?.message || 'Error al buscar clases');
      }
    });
  }

  toggleSelect(id: number): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
    this.allSelected = this.selectedIds.size === this.schedules.length && this.schedules.length > 0;
  }

  toggleSelectAll(): void {
    if (this.allSelected) {
      this.selectedIds.clear();
      this.allSelected = false;
    } else {
      this.schedules.forEach(s => this.selectedIds.add(s.id));
      this.allSelected = true;
    }
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  getDayName(dateStr: string): string {
    const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const date = new Date(datePart + 'T00:00:00');
    return dayNames[date.getDay()];
  }

  formatDisplayDate(dateStr: string): string {
    if (!dateStr) return '';
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const date = new Date(datePart + 'T00:00:00');
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  }

  formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  bulkDelete(): void {
    if (this.selectedCount === 0) return;

    const payload = {
      ids: Array.from(this.selectedIds),
      action: 'delete'
    };

    this.executeBulkAction(payload, 'Clases eliminadas correctamente');
    this.showDeleteConfirm = false;
  }

  bulkChangeStatus(): void {
    if (this.selectedCount === 0 || !this.bulkStatus) return;

    const payload = {
      ids: Array.from(this.selectedIds),
      action: 'update_status',
      status: this.bulkStatus
    };

    this.executeBulkAction(payload, 'Estado actualizado correctamente');
    this.bulkStatus = null;
  }

  bulkChangeVehicle(): void {
    if (this.selectedCount === 0 || !this.bulkVehicleId) return;

    const payload = {
      ids: Array.from(this.selectedIds),
      action: 'update_vehicle',
      vehicle_id: this.bulkVehicleId
    };

    this.executeBulkAction(payload, 'Vehiculo actualizado correctamente');
    this.bulkVehicleId = null;
  }

  bulkChangeTime(): void {
    if (this.selectedCount === 0 || !this.bulkStartTime || !this.bulkEndTime) return;

    const payload = {
      ids: Array.from(this.selectedIds),
      action: 'update_time',
      start_time: this.bulkStartTime,
      end_time: this.bulkEndTime
    };

    this.executeBulkAction(payload, 'Horario actualizado correctamente');
    this.bulkStartTime = '';
    this.bulkEndTime = '';
  }

  bulkChangeCapacity(): void {
    if (this.selectedCount === 0 || !this.bulkMaxStudents || this.bulkMaxStudents < 1) return;

    const payload = {
      ids: Array.from(this.selectedIds),
      action: 'update_capacity',
      max_students: this.bulkMaxStudents
    };

    this.executeBulkAction(payload, 'Capacidad actualizada correctamente');
    this.bulkMaxStudents = null;
  }

  private executeBulkAction(payload: any, successMessage: string): void {
    this.loadingAction = true;

    this.http.post<any>(`${environment.API_URL}/practice-schedules/bulk-update`, payload).subscribe({
      next: (res) => {
        this.loadingAction = false;
        this.toastr.success(res.message || successMessage);
        this.search();
      },
      error: (err) => {
        this.loadingAction = false;
        this.toastr.error(err.error?.message || 'Error al ejecutar la accion masiva');
      }
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Disponible':
        return 'bg-green-100 text-green-700';
      case 'Confirmada':
        return 'bg-blue-100 text-blue-700';
      case 'Cancelado':
        return 'bg-red-100 text-red-700';
      case 'Completo':
        return 'bg-purple-100 text-purple-700';
      case 'Reservado':
        return 'bg-yellow-100 text-yellow-700';
      case 'Completado':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  }

  isSelected(id: number): boolean {
    return this.selectedIds.has(id);
  }

  clearSelection(): void {
    this.selectedIds.clear();
    this.allSelected = false;
  }

  toggleDayFilter(dayFilter: { id: number; active: boolean }): void {
    dayFilter.active = !dayFilter.active;
    this.applyDaySelection();
  }

  private applyDaySelection(): void {
    const activeDays = this.dayFilterButtons.filter(d => d.active).map(d => d.id);

    if (activeDays.length === 0) {
      this.clearSelection();
      return;
    }

    this.selectedIds.clear();
    this.schedules.forEach(schedule => {
      const datePart = schedule.date.includes('T') ? schedule.date.split('T')[0] : schedule.date;
      const date = new Date(datePart + 'T00:00:00');
      if (activeDays.includes(date.getDay())) {
        this.selectedIds.add(schedule.id);
      }
    });
    this.allSelected = this.selectedIds.size === this.schedules.length && this.schedules.length > 0;
  }

  clearDayFilters(): void {
    this.dayFilterButtons.forEach(d => d.active = false);
  }

  get hasActiveDayFilters(): boolean {
    return this.dayFilterButtons.some(d => d.active);
  }
}
